import { useTransition, useDeferredValue, useMemo, useCallback, useState, useEffect, useRef } from 'react';

/**
 * Modern React Hooks for GoHustle Trading Application
 *
 * IMPORTANT: Infinite Loop Prevention Guidelines
 * ============================================
 *
 * 1. NEVER call setState directly inside useMemo callbacks
 * 2. NEVER call setState directly inside useCallback without proper conditions
 * 3. Always use useEffect for state updates that depend on calculated values
 * 4. Ensure useCallback dependencies are stable and don't change on every render
 * 5. Use Promise.resolve().then() for async state updates if needed
 * 6. Be careful with object/array dependencies - use useMemo for stable references
 *
 * Common Infinite Loop Patterns to AVOID:
 * - useMemo(() => { setState(...); return value; }, [deps])
 * - useEffect(() => setState(...), [stateValue]) where setState changes stateValue
 * - useCallback with unstable dependencies that change on every render
 */

// Hook for handling expensive option chain filtering with React 18 concurrent features
export const useOptionChainFiltering = (dataArray, searchTerm = '', filters = {}) => {
  const [isPending, startTransition] = useTransition();
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  // Defer the search term to prevent blocking urgent updates
  const deferredSearchTerm = useDeferredValue(localSearchTerm);

  // Filter option chain data with expensive operations
  const filteredData = useMemo(() => {
    if (!dataArray || dataArray.length === 0) return [];

    let filtered = dataArray;

    // Apply search term filter (deferred)
    if (deferredSearchTerm) {
      filtered = filtered.filter(item => {
        const strike = item.strikePrice?.toString() || '';
        const ceSymbol = item.CE?.tradingsymbol || '';
        const peSymbol = item.PE?.tradingsymbol || '';

        return (
          strike.includes(deferredSearchTerm) ||
          ceSymbol.toLowerCase().includes(deferredSearchTerm.toLowerCase()) ||
          peSymbol.toLowerCase().includes(deferredSearchTerm.toLowerCase())
        );
      });
    }

    // Apply additional filters
    if (filters.minOI) {
      filtered = filtered.filter(item =>
        (item.CE?.oi || 0) >= filters.minOI ||
        (item.PE?.oi || 0) >= filters.minOI
      );
    }

    if (filters.minVolume) {
      filtered = filtered.filter(item =>
        (item.CE?.volume || 0) >= filters.minVolume ||
        (item.PE?.volume || 0) >= filters.minVolume
      );
    }

    if (filters.onlyITM) {
      filtered = filtered.filter(item => item.isITM);
    }

    if (filters.onlyOTM) {
      filtered = filtered.filter(item => !item.isITM);
    }

    return filtered;
  }, [dataArray, deferredSearchTerm, filters]);

  // Update search term with transition to prevent blocking
  const updateSearchTerm = useCallback((newTerm) => {
    startTransition(() => {
      setLocalSearchTerm(newTerm);
    });
  }, [startTransition]);

  return {
    filteredData,
    isPending,
    updateSearchTerm,
    isFiltering: localSearchTerm !== deferredSearchTerm
  };
};

// Hook for handling large position data updates with transitions
export const usePositionsUpdates = (initialPositions = []) => {
  const [isPending, startTransition] = useTransition();
  const [positions, setPositions] = useState(initialPositions);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Deferred value for expensive sorting operations
  const deferredSortConfig = useDeferredValue(sortConfig);

  // Expensive sorting operation
  const sortedPositions = useMemo(() => {
    if (!deferredSortConfig.key) return positions;

    return [...positions].sort((a, b) => {
      let aValue = a[deferredSortConfig.key];
      let bValue = b[deferredSortConfig.key];

      // Handle numeric values
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return deferredSortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle string values
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return deferredSortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return 0;
    });
  }, [positions, deferredSortConfig]);

  // Update positions with transition
  const updatePositions = useCallback((newPositions) => {
    startTransition(() => {
      setPositions(newPositions);
    });
  }, [startTransition]);

  // Update sort configuration with transition
  const updateSort = useCallback((key) => {
    startTransition(() => {
      setSortConfig(prev => ({
        key,
        direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
      }));
    });
  }, [startTransition]);

  return {
    sortedPositions,
    isPending,
    updatePositions,
    updateSort,
    sortConfig: deferredSortConfig,
    isSorting: sortConfig !== deferredSortConfig
  };
};

// Hook for handling real-time data updates with batching
export const useRealTimeUpdates = (initialData, updateInterval = 1000) => {
  const [isPending, startTransition] = useTransition();
  const [data, setData] = useState(initialData);
  const [pendingUpdates, setPendingUpdates] = useState([]);

  // Deferred data for smooth UI updates
  const deferredData = useDeferredValue(data);

  // Batch updates to prevent excessive re-renders
  const addUpdate = useCallback((update) => {
    setPendingUpdates(prev => [...prev, update]);
  }, []);

  // Process batched updates with transition
  const processPendingUpdates = useCallback(() => {
    if (pendingUpdates.length === 0) return;

    startTransition(() => {
      setData(prevData => {
        let updatedData = { ...prevData };

        pendingUpdates.forEach(update => {
          if (update.type === 'merge') {
            updatedData = { ...updatedData, ...update.data };
          } else if (update.type === 'replace') {
            updatedData = update.data;
          } else if (update.type === 'array_merge' && Array.isArray(updatedData) && Array.isArray(update.data)) {
            updatedData = [...updatedData, ...update.data];
          }
        });

        return updatedData;
      });

      setPendingUpdates([]);
    });
  }, [pendingUpdates, startTransition]);

  return {
    data: deferredData,
    isPending,
    addUpdate,
    processPendingUpdates,
    hasPendingUpdates: pendingUpdates.length > 0,
    isUpdating: data !== deferredData
  };
};

// Hook for handling expensive calculations with React 18 features
export const useExpensiveCalculations = (rawData, calculationConfig) => {
  const [isPending, startTransition] = useTransition();
  const [calculationState, setCalculationState] = useState('idle');

  // Defer heavy calculation inputs
  const deferredData = useDeferredValue(rawData);
  const deferredConfig = useDeferredValue(calculationConfig);

  // Expensive calculations (P&L, Greeks, etc.)
  const calculatedData = useMemo(() => {
    if (!deferredData || !deferredConfig) return null;

    try {
      const result = {
        totalPnL: 0,
        totalValue: 0,
        greeks: {},
        riskMetrics: {},
        // Simulate expensive calculations
        processed: deferredData.map(item => {
          // Expensive P&L calculation
          const pnl = (item.ltp - item.averagePrice) * item.quantity;

          // Expensive Greeks calculation (mock)
          const delta = item.type === 'CE' ? 0.5 : -0.5;
          const gamma = 0.01;
          const theta = -0.05;
          const vega = 0.1;

          return {
            ...item,
            pnl,
            delta,
            gamma,
            theta,
            vega,
            riskScore: Math.abs(delta * item.quantity * item.ltp)
          };
        })
      };

      // Calculate totals
      result.totalPnL = result.processed.reduce((sum, item) => sum + item.pnl, 0);
      result.totalValue = result.processed.reduce((sum, item) => sum + (item.ltp * item.quantity), 0);

      return result;

    } catch (error) {
      console.error('Calculation error:', error);
      return null;
    }
  }, [deferredData, deferredConfig]);

  // Use useEffect to update calculation state without causing re-renders
  useEffect(() => {
    if (!deferredData || !deferredConfig) {
      setCalculationState('idle');
    } else if (calculatedData) {
      setCalculationState('completed');
    } else {
      setCalculationState('calculating');
    }
  }, [deferredData, deferredConfig, calculatedData]);

  // Trigger recalculation with transition
  const recalculate = useCallback(() => {
    startTransition(() => {
      setCalculationState('pending');
    });
  }, [startTransition]);

  return {
    calculatedData,
    isPending,
    calculationState,
    recalculate,
    isCalculating: calculationState === 'calculating' || isPending
  };
};

// Hook for managing concurrent UI updates
export const useConcurrentUI = () => {
  const [isPending, startTransition] = useTransition();
  const [urgentUpdates, setUrgentUpdates] = useState({});
  const [nonUrgentUpdates, setNonUrgentUpdates] = useState({});

  // Immediate urgent updates (user interactions)
  const updateUrgent = useCallback((key, value) => {
    setUrgentUpdates(prev => ({ ...prev, [key]: value }));
  }, []);

  // Deferred non-urgent updates (background data)
  const updateNonUrgent = useCallback((key, value) => {
    startTransition(() => {
      setNonUrgentUpdates(prev => ({ ...prev, [key]: value }));
    });
  }, [startTransition]);

  // Combined state with priority to urgent updates
  const combinedState = useMemo(() => ({
    ...nonUrgentUpdates,
    ...urgentUpdates
  }), [urgentUpdates, nonUrgentUpdates]);

  return {
    state: combinedState,
    isPending,
    updateUrgent,
    updateNonUrgent,
    hasUrgentUpdates: Object.keys(urgentUpdates).length > 0,
    hasNonUrgentUpdates: Object.keys(nonUrgentUpdates).length > 0
  };
};

/**
 * Development utility to detect potential infinite loops
 * This should only be used in development mode
 */
export const useInfiniteLoopDetection = (hookName, dependencies = []) => {
  const renderCountRef = useRef(0);
  const lastDependenciesRef = useRef(dependencies);

  useEffect(() => {
    renderCountRef.current += 1;

    // Check if dependencies changed
    const dependenciesChanged = !dependencies.every(
      (dep, index) => dep === lastDependenciesRef.current[index]
    );

    if (dependenciesChanged) {
      renderCountRef.current = 1; // Reset count on legitimate dependency change
      lastDependenciesRef.current = dependencies;
    }

    // Warn if excessive re-renders detected
    if (renderCountRef.current > 10) {
      console.warn(
        `🚨 Potential infinite loop detected in ${hookName}!`,
        `Render count: ${renderCountRef.current}`,
        'Dependencies:', dependencies
      );
    }
  });

  return renderCountRef.current;
};

// Export all hooks
const modernReactHooks = {
  useOptionChainFiltering,
  usePositionsUpdates,
  useRealTimeUpdates,
  useExpensiveCalculations,
  useConcurrentUI,
  useInfiniteLoopDetection
};

export default modernReactHooks;